import React from "react";

/**
 * ComingSoon — Sovereign refit placeholder.
 * Deep space backdrop · breathing gold logo · Montserrat display.
 */
export default function ComingSoon() {
  return (
    <main data-testid="coming-soon-root" className="relative min-h-screen overflow-hidden bg-[var(--sv-bg)] text-[var(--sv-text)]">
      {/* Aurora layers */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 cs-aurora" />
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 cs-starfield" />
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 cs-grid" />

      {/* Top hairline strip */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-[var(--sv-border)]">
        <div className="flex items-baseline gap-2">
          <span className="font-sans-dsp text-[12px] font-extrabold tracking-[0.32em] text-[var(--sv-text)]">EIGOO</span>
          <span className="text-[var(--sv-gold)] text-xs">·</span>
          <span className="font-sans-dsp text-[11px] font-bold tracking-[0.32em] text-[var(--sv-gold)]">FLEET</span>
        </div>
        <div className="flex items-center gap-2" data-testid="refit-status">
          <span className="sv-pulse-dot" />
          <span className="font-mono-num text-[10px] font-semibold tracking-[0.22em] uppercase text-[var(--sv-text-2)]">
            Refit · in progress
          </span>
        </div>
      </div>

      {/* Center stage */}
      <section className="relative z-10 flex flex-col items-center justify-center text-center px-6 md:px-12" style={{ minHeight: "calc(100vh - 120px)" }}>
        {/* Glyph */}
        <div className="mb-10 cs-breathe" data-testid="hero-glyph">
          <svg width="88" height="88" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="11" fill="none" stroke="var(--sv-gold-muted)" strokeWidth="0.4" />
            <circle cx="12" cy="12" r="7"  fill="none" stroke="var(--sv-gold-muted)" strokeWidth="0.4" />
            <circle cx="12" cy="12" r="3"  fill="none" stroke="var(--sv-gold)" strokeWidth="0.5" />
            <circle cx="12" cy="12" r="1.4" fill="var(--sv-gold)" />
            <g className="sv-sweep" style={{ transformOrigin: "12px 12px" }}>
              <path d="M12 12 L12 1 A11 11 0 0 1 21.5 7 Z" fill="url(#cs-sweep)" opacity="0.85" />
              <defs>
                <linearGradient id="cs-sweep" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"  stopColor="var(--sv-gold)" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="var(--sv-gold)" stopOpacity="0" />
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>

        {/* Overline */}
        <div className="overline mb-5" data-testid="overline">
          Sovereign Refit · Q3 · 2026
        </div>

        {/* Header */}
        <h1
          data-testid="hero-headline"
          className="cs-breathe-text font-sans-dsp uppercase text-[var(--sv-gold)]"
          style={{
            fontSize: "clamp(38px, 7vw, 96px)",
            fontWeight: 900,
            letterSpacing: "0.08em",
            lineHeight: 1.05,
          }}
        >
          EIGOO <span className="text-[var(--sv-gold-muted)]">·</span> Trade Vessel
        </h1>

        {/* Gold hairline divider */}
        <div className="my-8 flex items-center gap-3" aria-hidden>
          <span className="h-px w-12 bg-[var(--sv-gold-border)]" />
          <span className="h-1.5 w-1.5 bg-[var(--sv-gold)]" />
          <span className="h-px w-12 bg-[var(--sv-gold-border)]" />
        </div>

        {/* Sub-header */}
        <p
          data-testid="hero-subhead"
          className="max-w-2xl font-sans-dsp text-[var(--sv-text-2)]"
          style={{
            fontSize: "clamp(15px, 1.4vw, 20px)",
            fontWeight: 500,
            letterSpacing: "0.04em",
            lineHeight: 1.55,
          }}
        >
          Refitting for <span className="text-[var(--sv-text)] font-bold">Q3 2026</span>.<br className="hidden md:inline" />
          The signal is temporarily silenced.
        </p>

        {/* CTA — Partners Hub ingress */}
        <a
          data-testid="partners-cta"
          href="https://eigooinc.net/partners"
          target="_blank"
          rel="noopener noreferrer"
          className="cs-cta mt-10 inline-flex items-center justify-center"
          aria-label="Check out our partners"
        >
          <span className="cs-cta-label">[ &nbsp;Check out our Partners&nbsp; ]</span>
        </a>

        {/* Decorative coordinate readout */}
        <div className="mt-10 flex items-center gap-6" data-testid="coordinate-readout">
          <CoordCol label="Vessel" value="TRADE · 01" />
          <span className="h-6 w-px bg-[var(--sv-border)]" />
          <CoordCol label="Status" value="DRY · DOCK" />
          <span className="h-6 w-px bg-[var(--sv-border)]" />
          <CoordCol label="ETA" value="Q3 · 26" />
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[var(--sv-border)] px-6 md:px-12 py-5 flex items-center justify-between">
        <span className="font-mono-num text-[10px] font-semibold tracking-[0.28em] uppercase text-[var(--sv-text-3)]">
          //  Channel closed  ·  monitoring resumes Q3
        </span>
        <span
          data-testid="footer-attribution"
          className="font-sans-dsp text-[11px] tracking-[0.34em] uppercase text-[var(--sv-gold)]"
        >
          Propelled by <span className="text-[var(--sv-text)] font-extrabold">EIGOO INC.</span>
        </span>
      </footer>
    </main>
  );
}

function CoordCol({ label, value }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="overline">{label}</span>
      <span className="font-mono-num text-[12px] text-[var(--sv-text)] tracking-[0.14em]">{value}</span>
    </div>
  );
}
